// Maternity and MCH (docs/plans/phase2-maternity-mch.md §3.2). Internal IDs only in paths;
// measured values, danger signs and notes travel in bodies.
import { queryOptions } from '@tanstack/react-query';
import {
  ancViewSchema,
  childHealthViewSchema,
  labourOpenedSchema,
  labourRecordViewSchema,
  labourWardSchema,
  maternitySettingsSchema,
  maternityViewSchema,
  vaccinesViewSchema,
  type ClosePregnancyBody,
  type CorrectPregnancyBody,
  type DeliveryBirthNotificationBody,
  type ImmunisationScheduleBody,
  type MaternitySettingsBody,
  type OpenLabourBody,
  type CloseLabourBody,
  type openPregnancyBodySchema,
  type recordAncContactBodySchema,
  type recordGrowthBodySchema,
  type RecordDeliveryBody,
  type RecordImmunisationBody,
  type recordLabourObservationBodySchema,
  type recordPostnatalCheckBodySchema,
  type VaccineBody,
} from '@wodi/shared';
import type { z } from 'zod';

import { api } from './client.ts';

/** The labour ward list refreshes every minute, like the ward worklist. */
export const LABOUR_WARD_REFRESH_MS = 60_000;

export const maternityKeys = {
  all: ['maternity'] as const,
  labourWard: ['maternity', 'labour-ward'] as const,
  patient: (patientId: string) => ['maternity', 'patient', patientId] as const,
  anc: (visitId: string) => ['maternity', 'anc', visitId] as const,
  labour: (id: string) => ['maternity', 'labour', id] as const,
  childHealth: (patientId: string) => ['maternity', 'child-health', patientId] as const,
  settings: ['maternity', 'settings'] as const,
  vaccines: ['maternity', 'vaccines'] as const,
};

export const labourWardQuery = queryOptions({
  queryKey: maternityKeys.labourWard,
  queryFn: () => api('GET', '/maternity/labour-ward', { schema: labourWardSchema }),
  refetchInterval: LABOUR_WARD_REFRESH_MS,
});

export const maternityQuery = (patientId: string) =>
  queryOptions({
    queryKey: maternityKeys.patient(patientId),
    queryFn: () => api('GET', `/patients/${patientId}/maternity`, { schema: maternityViewSchema }),
  });

export const ancQuery = (visitId: string) =>
  queryOptions({
    queryKey: maternityKeys.anc(visitId),
    queryFn: () => api('GET', `/visits/${visitId}/anc`, { schema: ancViewSchema }),
  });

export const labourQuery = (id: string) =>
  queryOptions({
    queryKey: maternityKeys.labour(id),
    queryFn: () => api('GET', `/labour-records/${id}`, { schema: labourRecordViewSchema }),
  });

export const childHealthQuery = (patientId: string) =>
  queryOptions({
    queryKey: maternityKeys.childHealth(patientId),
    queryFn: () =>
      api('GET', `/patients/${patientId}/child-health`, { schema: childHealthViewSchema }),
  });

export const maternitySettingsQuery = queryOptions({
  queryKey: maternityKeys.settings,
  queryFn: () => api('GET', '/settings/maternity', { schema: maternitySettingsSchema }),
});

export const vaccinesQuery = queryOptions({
  queryKey: maternityKeys.vaccines,
  queryFn: () => api('GET', '/settings/vaccines', { schema: vaccinesViewSchema }),
});

// --- Pregnancies --------------------------------------------------------------------------------

export const openPregnancy = (visitId: string, body: z.input<typeof openPregnancyBodySchema>) =>
  api('POST', `/visits/${visitId}/pregnancies`, { body, schema: maternityViewSchema });

export const correctPregnancy = (id: string, body: CorrectPregnancyBody) =>
  api('POST', `/pregnancies/${id}/corrections`, { body, schema: maternityViewSchema });

export const closePregnancy = (id: string, body: ClosePregnancyBody) =>
  api('POST', `/pregnancies/${id}/close`, { body, schema: maternityViewSchema });

export const pregnancyInError = (id: string, reason: string) =>
  api('POST', `/pregnancies/${id}/entered-in-error`, {
    body: { reason },
    schema: maternityViewSchema,
  });

// --- ANC ----------------------------------------------------------------------------------------

export const recordAncContact = (
  visitId: string,
  body: z.input<typeof recordAncContactBodySchema>,
) => api('POST', `/visits/${visitId}/anc-contacts`, { body, schema: ancViewSchema });

export const ancContactInError = (id: string, reason: string) =>
  api('POST', `/anc-contacts/${id}/entered-in-error`, { body: { reason }, schema: ancViewSchema });

// --- Labour, delivery and postnatal ------------------------------------------------------------

export const openLabour = (admissionId: string, body: OpenLabourBody) =>
  api('POST', `/admissions/${admissionId}/labour`, { body, schema: labourOpenedSchema });

export const recordLabourObservation = (
  id: string,
  body: z.input<typeof recordLabourObservationBodySchema>,
) => api('POST', `/labour-records/${id}/observations`, { body, schema: labourRecordViewSchema });

export const labourObservationInError = (id: string, reason: string) =>
  api('POST', `/labour-observations/${id}/entered-in-error`, {
    body: { reason },
    schema: labourRecordViewSchema,
  });

export const closeLabour = (id: string, body: CloseLabourBody) =>
  api('POST', `/labour-records/${id}/close`, { body, schema: labourRecordViewSchema });

export const labourInError = (id: string, reason: string) =>
  api('POST', `/labour-records/${id}/entered-in-error`, {
    body: { reason },
    schema: labourRecordViewSchema,
  });

export const recordDelivery = (id: string, body: RecordDeliveryBody) =>
  api('POST', `/labour-records/${id}/delivery`, { body, schema: labourRecordViewSchema });

export const deliveryInError = (id: string, reason: string) =>
  api('POST', `/deliveries/${id}/entered-in-error`, {
    body: { reason },
    schema: labourRecordViewSchema,
  });

export const startBirthNotification = (babyId: string, body: DeliveryBirthNotificationBody) =>
  api('POST', `/delivery-babies/${babyId}/birth-notification`, {
    body,
    schema: labourRecordViewSchema,
  });

export const recordPostnatalCheck = (
  deliveryId: string,
  body: z.input<typeof recordPostnatalCheckBodySchema>,
) =>
  api('POST', `/deliveries/${deliveryId}/postnatal-checks`, {
    body,
    schema: labourRecordViewSchema,
  });

export const postnatalCheckInError = (id: string, reason: string) =>
  api('POST', `/postnatal-checks/${id}/entered-in-error`, {
    body: { reason },
    schema: labourRecordViewSchema,
  });

// --- Immunisation and growth --------------------------------------------------------------------

export const recordImmunisation = (visitId: string, body: RecordImmunisationBody) =>
  api('POST', `/visits/${visitId}/immunisations`, { body, schema: childHealthViewSchema });

export const immunisationInError = (id: string, reason: string) =>
  api('POST', `/immunisations/${id}/entered-in-error`, {
    body: { reason },
    schema: childHealthViewSchema,
  });

export const recordGrowth = (visitId: string, body: z.input<typeof recordGrowthBodySchema>) =>
  api('POST', `/visits/${visitId}/growth`, { body, schema: childHealthViewSchema });

// --- Settings -----------------------------------------------------------------------------------

export const saveMaternitySettings = (body: MaternitySettingsBody) =>
  api('PUT', '/settings/maternity', { body, schema: maternitySettingsSchema });

export const createVaccine = (body: VaccineBody) =>
  api('POST', '/settings/vaccines', { body, schema: vaccinesViewSchema });

export const updateVaccine = (id: string, body: VaccineBody) =>
  api('PUT', `/settings/vaccines/${id}`, { body, schema: vaccinesViewSchema });

export const saveImmunisationSchedule = (body: ImmunisationScheduleBody) =>
  api('PUT', '/settings/immunisation-schedule', { body, schema: vaccinesViewSchema });
